/* Programs.jsx -- Programs page redesign per CR #140.
   Map is no longer the centerpiece. Programs are grouped by category and
   shown as colorful editorial cards. Clicking a card opens a modal with a
   small Leaflet map zoomed to that program's location, plus the description
   and the rec center's contact info. */

// CR #301: Acting + Other (cheerleading etc.) added so JJ's bulk
// submission programs render in the grouped grid and have map-pin
// colors. Without them, Acting/Other rows would be silently hidden.
// CR #326: "Acting" filter chip removed per JJ -- no program is type
// "Acting" (the chip rendered at count 0), so dropping it hides nothing.
// CR #342 (CR-72142): "Other" filter chip removed per JJ. After #316
// (Cheerleading), #333 (Literature -> migration 023) split everything out of
// the catch-all, no live program is type "Other" (verified 0 rows), so the
// chip rendered at count 0 and dropping it hides nothing.
const CATEGORIES = [
  { id: "Dance",   label: "Dance",   color: "#D8552B", chipClass: "dance" },
  { id: "Theater", label: "Theater", color: "#C28215", chipClass: "musical" },
  { id: "Music",   label: "Music",   color: "#7A8B5C", chipClass: "singing" },
  // CR #316: Cheerleading split out of "Other" so JJ's cheer squads
  // (e.g. Northern Hunter Elite Cheer) get their own filter chip, grouped
  // section, and pin color instead of being lumped into the catch-all.
  { id: "Cheerleading", label: "Cheerleading", color: "#C24E80", chipClass: "cheer" },
  // CR #333: Literature split out of "Other" per JJ -- the Schmidt Playground
  // "Literature + Speech" program (the lone "Other" row) now gets its own
  // filter chip, grouped section, and violet map-pin color. The program's
  // type is flipped Other -> Literature in migration 023.
  { id: "Literature", label: "Literature", color: "#6E4FA0", chipClass: "literature" },
];

function colorFor(type) {
  const m = CATEGORIES.find(c => c.id === type);
  return m ? m.color : "#2A1820";
}

// DB rows from /api/programs have different field names + numeric lat/lng;
// normalize so cards/modal don't need to know about both shapes.
// CR #302: Number(badRow.lat) used to yield NaN which slipped past the
// != null check and gave Leaflet [NaN, NaN] pins (silently dropped).
// Treat NaN/Infinity as "no coords" so bad DB rows can't break the map.
function normalizeDbProgram(row) {
  const rawLat = row.lat != null ? Number(row.lat) : null;
  const rawLng = row.lng != null ? Number(row.lng) : null;
  const lat = Number.isFinite(rawLat) ? rawLat : null;
  const lng = Number.isFinite(rawLng) ? rawLng : null;
  return {
    id:          row.slug ? `db-${row.slug}` : `db-${row.id}`,
    name:        row.name,
    type:        row.type,
    center:      row.center_name,
    region:      row.region || "Philadelphia",
    coords:      (lat != null && lng != null) ? [lat, lng] : null,
    day:         row.day || row.time_text || "",
    phone:       row.contact_phone || "",
    email:       row.contact_email || "",
    description: row.description || "",
    address:     row.address || "",
    featured:    row.featured === true,
  };
}

function ProgramsPage() {
  const [filter, setFilter] = React.useState("all");
  const [regionFilter, setRegionFilter] = React.useState("all");
  const [selected, setSelected] = React.useState(null);
  // CR #301: the "under construction" notice was removed once JJ's
  // bulk programs landed -- the page now ships real listings.
  // DB-side approved programs (from /api/programs). Merged with the seed
  // PROGRAMS in data.js so the original 10 always show even before the
  // first center submission, and newly-approved center submissions appear
  // here without a code change.
  const [dbPrograms, setDbPrograms] = React.useState([]);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/programs", { headers: { accept: "application/json" } })
      .then(r => r.ok ? r.json() : { ok: false, rows: [] })
      .then(j => {
        if (cancelled) return;
        const rows = Array.isArray(j.rows) ? j.rows : [];
        setDbPrograms(rows.map(normalizeDbProgram));
      })
      .catch(() => { /* fall back silently to seed-only */ });
    return () => { cancelled = true; };
  }, []);

  // Seed first (stable order, original 10), then any newly-approved
  // submissions appended.
  const programs = [...(window.PROGRAMS || []), ...dbPrograms];

  // Region chips are derived from the data so newly-approved center
  // submissions in a previously-unseen region don't require a code
  // change to surface in the filter row.
  const allRegions = React.useMemo(() => {
    const set = new Set();
    programs.forEach(p => { if (p.region) set.add(p.region); });
    return Array.from(set).sort();
  }, [programs]);

  const filtered = programs.filter(p =>
    (filter === "all" || p.type === filter) &&
    (regionFilter === "all" || p.region === regionFilter)
  );

  const grouped = CATEGORIES.map(cat => ({
    ...cat,
    items: featuredFirst(filtered.filter(p => p.type === cat.id))
  })).filter(g => g.items.length > 0);

  const selectedProgram = selected ? programs.find(p => p.id === selected) : null;

  return (
    <div>
      <section style={{ background: "var(--c-cream)", padding: "72px 0 32px" }}>
        <div className="container">
          <div className="eyebrow">Year-round Programs</div>
          <h1 className="display" style={{ fontSize: "clamp(48px, 8vw, 108px)", margin: "12px 0 18px", lineHeight: 0.95 }}>
            Find a program<br />near home.
          </h1>
          <p style={{ fontSize: 18, maxWidth: 640, color: "var(--c-ink-soft)", lineHeight: 1.5 }}>
            Low-cost rec center programs in dance, theater, and music.
            Tap a program to see the location, schedule, and how to sign up.
          </p>
        </div>
      </section>

      <section className="section-tight">
        <div className="container">
          <div className="pgm-filter-stack">
            <div className="pgm-filter-row">
              <div className="pgm-filter-label">Type</div>
              <div className="pgm-filter-chips">
                <FilterChip
                  active={filter === "all"} onClick={() => setFilter("all")}
                  color="var(--c-ink)" label="All" count={programs.length}
                />
                {CATEGORIES.map(c => (
                  <FilterChip
                    key={c.id}
                    active={filter === c.id} onClick={() => setFilter(c.id)}
                    color={c.color} label={c.label}
                    count={programs.filter(p => p.type === c.id).length}
                  />
                ))}
              </div>
            </div>
            {allRegions.length > 1 && (
              <div className="pgm-filter-row">
                <div className="pgm-filter-label">Region</div>
                <div className="pgm-filter-chips">
                  <FilterChip
                    active={regionFilter === "all"} onClick={() => setRegionFilter("all")}
                    color="var(--c-ink)" label="All" count={programs.length}
                  />
                  {allRegions.map(r => (
                    <FilterChip
                      key={r}
                      active={regionFilter === r} onClick={() => setRegionFilter(r)}
                      color="var(--c-ink)" label={r}
                      count={programs.filter(p => p.region === r).length}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CR #301: rec-center map below filters, above the grouped
              program cards. Mirrors the committee-map pattern on the
              About page. One pin per center, colored by the dominant
              program type at that center. Click a pin -> the side
              panel lists every program at that center. */}
          <CenterMap programs={filtered} typeFilter={filter} onSelect={setSelected} />

          {grouped.map(group => (
            <CategorySection key={group.id} group={group} onSelect={setSelected} />
          ))}

          {grouped.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--c-ink-soft)", fontFamily: "var(--f-mono)", fontSize: 13, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              No programs in this category yet.
            </div>
          )}
        </div>
      </section>

      {selectedProgram && (
        <ProgramModal program={selectedProgram} onClose={() => setSelected(null)} />
      )}

      <style>{`
        .programs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
        }
        @media (max-width: 980px) { .programs-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px) { .programs-grid { grid-template-columns: 1fr; } }

        .pgm-card {
          /* CR #281: pin the button's text color so Safari/iOS doesn't
             render the unstyled <button> in -apple-system-blue. The card
             title (.pgm-name) inherits this. */
          color: var(--c-ink);
          border: 2px solid var(--c-ink);
          border-radius: 18px;
          background: var(--c-paper);
          padding: 22px 22px 20px;
          cursor: pointer;
          text-align: left;
          width: 100%;
          font: inherit;
          box-shadow: 4px 4px 0 var(--c-ink);
          transition: transform 140ms ease, box-shadow 140ms ease;
          display: flex; flex-direction: column; gap: 10px;
          position: relative;
          overflow: hidden;
        }
        .pgm-card:hover, .pgm-card:focus-visible {
          transform: translate(-3px, -3px);
          box-shadow: 7px 7px 0 var(--c-ink);
          outline: none;
        }
        .pgm-card::before {
          content: "";
          position: absolute; top: 0; left: 0; right: 0; height: 6px;
          background: var(--pgm-accent, var(--c-primary));
        }
        .pgm-featured-tag {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 2px 8px;
          border-radius: 999px;
          background: var(--c-ink);
          color: var(--c-paper);
          font-family: var(--f-mono);
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          vertical-align: middle;
        }
        .pgm-card .pgm-name {
          font-family: var(--f-display);
          font-weight: 700;
          font-size: 22px;
          line-height: 1.1;
          margin-top: 4px;
        }
        .pgm-card .pgm-meta {
          font-family: var(--f-mono);
          font-size: 10.5px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--c-ink-soft);
          margin-top: auto;
        }
        .pgm-card .pgm-hint {
          font-family: var(--f-mono);
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--pgm-accent);
          margin-top: 6px;
          display: flex; align-items: center; gap: 6px;
        }

        .pgm-section-head {
          display: flex; align-items: baseline; gap: 14px;
          margin: 28px 0 18px;
          padding-bottom: 10px;
          border-bottom: 2px solid var(--c-ink);
        }
        .pgm-section-head .pgm-section-title {
          font-family: var(--f-display);
          font-weight: 800;
          font-size: clamp(28px, 4vw, 40px);
          line-height: 1;
        }
        .pgm-section-head .pgm-section-count {
          font-family: var(--f-mono);
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--c-ink-soft);
        }
        .pgm-section-head::before {
          content: "";
          width: 14px; height: 14px;
          border-radius: 50%;
          background: var(--pgm-accent);
          border: 2px solid var(--c-ink);
          align-self: center;
        }

        .pgm-filter-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 10px 16px; min-height: 44px;
          border: 2px solid var(--c-ink);
          border-radius: 999px;
          background: var(--c-paper);
          color: var(--c-ink);
          font-family: var(--f-display);
          font-weight: 600; font-size: 14px;
          cursor: pointer;
          transition: background 120ms;
        }
        .pgm-filter-chip:hover { background: var(--c-cream-deep); }
        .pgm-filter-chip.is-active { background: var(--chip-color); color: var(--c-paper); border-color: var(--chip-color); }
        .pgm-filter-chip .pgm-filter-count {
          font-family: var(--f-mono); font-size: 10.5px; opacity: 0.75;
        }

        /* Two-row filter stack: Type chips on top, Region chips below.
           Each row has a small uppercase label so it's obvious which
           dimension you're filtering. The region row is data-driven so
           a newly-approved program in a previously-unseen region adds
           a chip without a code change. */
        .pgm-filter-stack {
          display: flex; flex-direction: column; gap: 14px;
          margin-bottom: 36px;
        }
        .pgm-filter-row {
          display: grid;
          grid-template-columns: 88px 1fr;
          gap: 12px;
          align-items: center;
        }
        .pgm-filter-label {
          font-family: var(--f-mono);
          font-size: 10.5px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--c-ink-soft);
          align-self: center;
        }
        .pgm-filter-chips {
          display: flex; gap: 8px; flex-wrap: wrap;
        }
        @media (max-width: 640px) {
          .pgm-filter-row { grid-template-columns: 1fr; gap: 6px; }
          .pgm-filter-chips {
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: 0 -16px;
            padding: 4px 16px;
            scroll-snap-type: x proximity;
          }
          .pgm-filter-chips::-webkit-scrollbar { display: none; }
          .pgm-filter-chip { flex-shrink: 0; scroll-snap-align: start; }
        }
      `}</style>
    </div>
  );
}

// CR #301: CenterMap renders one pin per unique rec center (grouped by
// center_name) on a Leaflet map matching the committee-map pattern in
// About.jsx. Pin color = the dominant program type at that center.
// Click a pin -> opens the side panel listing every program there.
// Featured programs (programs.featured, migration 042) get a bigger starred
// pin, sort first in the side panel and their category grid, and their
// center opens in the side panel when someone picks that type chip.
function featuredFirst(list) {
  return list.slice().sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
}

const STAR_SVG = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z"/></svg>';

function FeaturedTag() {
  return (
    <span className="pgm-featured-tag">
      <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z" />
      </svg>
      Featured
    </span>
  );
}

function CenterMap({ programs, typeFilter, onSelect }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const [selectedCenter, setSelectedCenter] = React.useState(null);

  // Group by center_name; keep first lat/lng we see for that center.
  const centers = React.useMemo(() => {
    const byCenter = new Map();
    programs.forEach(p => {
      const center = p.center;
      if (!center || !p.coords) return;
      if (!byCenter.has(center)) {
        byCenter.set(center, {
          name: center,
          coords: p.coords,
          address: p.address || "",
          region: p.region || "",
          programs: [],
          typeCounts: {},
        });
      }
      const c = byCenter.get(center);
      c.programs.push(p);
      c.typeCounts[p.type] = (c.typeCounts[p.type] || 0) + 1;
    });
    return Array.from(byCenter.values()).map(c => {
      // Dominant type wins the pin color (ties broken by CATEGORIES order).
      let dominantType = "Dance";
      let max = 0;
      CATEGORIES.forEach(cat => {
        const count = c.typeCounts[cat.id] || 0;
        if (count > max) { max = count; dominantType = cat.id; }
      });
      return {
        ...c,
        programs: featuredFirst(c.programs),
        featured: c.programs.some(p => p.featured),
        dominantType,
        color: colorFor(dominantType),
      };
    });
  }, [programs]);

  // Init Leaflet once, AFTER the canvas exists in the DOM. The component
  // returns null when centers is empty (line below), so on first mount the
  // container ref is null. Effect re-runs when centers go from empty to
  // non-empty (typically when /api/programs resolves), at which point the
  // canvas div has mounted and we can init Leaflet on it. mapRef.current
  // guard prevents re-init if centers toggle back via filtering.
  const hasCenters = centers.length > 0;
  React.useEffect(() => {
    if (!hasCenters) return;
    let cancelled = false;
    function init() {
      if (cancelled || !window.L || !containerRef.current || mapRef.current) return;
      const L = window.L;
      const map = L.map(containerRef.current, {
        center: [40.02, -75.10],
        zoom: 11,
        zoomControl: true,
        attributionControl: true,
        scrollWheelZoom: false,
      });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(map);
      mapRef.current = { map, layer: L.layerGroup().addTo(map) };
    }
    if (window.L) init();
    else {
      const interval = setInterval(() => { if (window.L) { clearInterval(interval); init(); } }, 50);
      return () => {
        cancelled = true;
        clearInterval(interval);
        if (mapRef.current) { mapRef.current.map.remove(); mapRef.current = null; }
      };
    }
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.map.remove(); mapRef.current = null; }
    };
  }, [hasCenters]);

  // Picking a type chip opens the center of a featured program of that type,
  // so someone looking for music lessons lands on the featured one first.
  const featuredCenter = typeFilter && typeFilter !== "all"
    ? centers.find(c => c.programs.some(p => p.featured && p.type === typeFilter))
    : null;
  const featuredCenterName = featuredCenter ? featuredCenter.name : null;
  React.useEffect(() => {
    if (featuredCenter) setSelectedCenter(featuredCenter);
  }, [typeFilter, featuredCenterName]);

  // Redraw pins whenever the filtered center list changes.
  React.useEffect(() => {
    if (!mapRef.current || !window.L) return;
    const { layer, map } = mapRef.current;
    layer.clearLayers();
    const L = window.L;
    centers.forEach(c => {
      const icon = c.featured
        ? L.divIcon({
            className: "pgm-pin",
            html: `<div class="pgm-pin-body pgm-pin-featured" style="background:${c.color}">${STAR_SVG}</div>`,
            iconSize: [32, 32],
            iconAnchor: [16, 16],
          })
        : L.divIcon({
            className: "pgm-pin",
            html: `<div class="pgm-pin-body" style="background:${c.color}"><div class="pgm-pin-dot"></div></div>`,
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          });
      L.marker(c.coords, { icon, zIndexOffset: c.featured ? 1000 : 0, title: c.name })
        .addTo(layer).on("click", () => setSelectedCenter(c));
    });
    // Fit the map to the pin extent when there are pins so JJ's bulk
    // submission (mostly far-northeast) doesn't get cropped under the
    // default Center City viewport.
    if (centers.length > 0) {
      const bounds = L.latLngBounds(centers.map(c => c.coords)).pad(0.18);
      map.fitBounds(bounds, { animate: false });
    }
  }, [centers]);

  if (centers.length === 0) return null;

  return (
    <div style={{ margin: "12px 0 32px" }}>
      <div className="pgm-map-grid">
        <div
          ref={containerRef}
          className="pgm-map-canvas"
          style={{
            height: 420,
            borderRadius: 18, overflow: "hidden",
            border: "2px solid var(--c-ink)",
            boxShadow: "var(--shadow-lift)",
            background: "var(--c-cream-deep)",
            // CR #316: isolate Leaflet's z-index stack (its panes/controls go
            // up to ~1000) into its own stacking context so it can't paint
            // over the ProgramModal (z-index 100) when a program is opened.
            // Same fix the About-page committee map got in CR #237.
            position: "relative",
            isolation: "isolate",
          }}
        />
        <div className="pgm-map-aside">
          {selectedCenter ? (
            <div>
              <div className="eyebrow" style={{ color: selectedCenter.color }}>
                {selectedCenter.region || "Philadelphia"}
              </div>
              <div className="display" style={{ fontSize: 26, lineHeight: 1.1, marginTop: 6 }}>
                {selectedCenter.name}
              </div>
              {selectedCenter.address && (
                <div style={{ fontFamily: "var(--f-mono)", fontSize: 12, color: "var(--c-ink-soft)", marginTop: 6, letterSpacing: "0.04em" }}>
                  {selectedCenter.address}
                </div>
              )}
              <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedCenter.programs.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelect(p.id)}
                    className="pgm-map-program-row"
                    style={{ borderLeft: `4px solid ${colorFor(p.type)}` }}
                  >
                    <div className="pgm-map-program-name">
                      {p.name}
                      {p.featured && <FeaturedTag />}
                    </div>
                    <div className="pgm-map-program-meta">
                      <span>{p.type}</span>
                      {p.day && <span> &middot; {p.day}</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ color: "var(--c-ink-soft)" }}>
              <div className="eyebrow">{centers.length} center{centers.length === 1 ? "" : "s"}</div>
              <div className="display" style={{ fontSize: 22, lineHeight: 1.15, marginTop: 6 }}>
                Tap a pin to see programs at that rec center.
              </div>
              <p style={{ fontSize: 14, marginTop: 12, lineHeight: 1.55 }}>
                Pin color matches the dominant program type at each center.
                Filters above narrow the map too.
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pgm-map-grid {
          display: grid;
          grid-template-columns: 1.4fr 1fr;
          gap: 22px;
          align-items: stretch;
        }
        @media (max-width: 900px) {
          .pgm-map-grid { grid-template-columns: 1fr; }
        }
        .pgm-map-aside {
          background: var(--c-paper);
          border: 2px solid var(--c-ink);
          border-radius: 18px;
          padding: 24px;
          box-shadow: var(--shadow-lift);
          min-height: 0;
          overflow: auto;
        }
        .pgm-map-program-row {
          background: var(--c-cream);
          border: 1px solid var(--c-ink-soft);
          border-left-width: 4px;
          border-radius: 8px;
          padding: 10px 12px;
          text-align: left;
          cursor: pointer;
          transition: transform 0.15s ease;
        }
        .pgm-map-program-row:hover { transform: translateX(2px); }
        .pgm-map-program-name {
          font-weight: 600;
          color: var(--c-ink);
          font-size: 14px;
        }
        .pgm-map-program-meta {
          font-family: var(--f-mono);
          font-size: 11px;
          color: var(--c-ink-soft);
          letter-spacing: 0.04em;
          text-transform: uppercase;
          margin-top: 2px;
        }
        .pgm-pin-body {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(0,0,0,0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
        .pgm-pin-featured {
          width: 32px;
          height: 32px;
          border-width: 3px;
          color: #fff;
          box-shadow: 0 0 0 3px rgba(0,0,0,0.18), 0 3px 8px rgba(0,0,0,0.3);
        }
        .pgm-map-program-name .pgm-featured-tag { margin-left: 8px; }
        .pgm-pin-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #fff;
        }
      `}</style>
    </div>
  );
}

function FilterChip({ active, onClick, color, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pgm-filter-chip ${active ? "is-active" : ""}`}
      style={{ "--chip-color": color }}
    >
      {label}
      <span className="pgm-filter-count">{count}</span>
    </button>
  );
}

function CategorySection({ group, onSelect }) {
  return (
    <div style={{ "--pgm-accent": group.color }}>
      <div className="pgm-section-head">
        <div className="pgm-section-title">{group.label}</div>
        <div className="pgm-section-count">{group.items.length} programs</div>
      </div>
      <div className="programs-grid">
        {group.items.map(p => (
          <ProgramCard key={p.id} program={p} accent={group.color} onClick={() => onSelect(p.id)} />
        ))}
      </div>
    </div>
  );
}

function ProgramCard({ program, accent, onClick }) {
  return (
    <button type="button" className="pgm-card" onClick={onClick} style={{ "--pgm-accent": accent }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
        <span className={`chip ${categoryChipClass(program.type)}`}>
          {program.type}
        </span>
        {program.featured && <FeaturedTag />}
      </div>
      <div className="pgm-name">{program.name}</div>
      <div className="pgm-meta">
        {program.center} &middot; {program.day}
      </div>
      <div className="pgm-hint">
        Details &amp; location <span aria-hidden="true">&rarr;</span>
      </div>
    </button>
  );
}

function categoryChipClass(type) {
  if (type === "Dance") return "dance";
  if (type === "Theater") return "musical";
  if (type === "Music") return "singing";
  return "";
}

// Build a Google Maps directions URL. Prefer coordinates when available
// (most precise), fall back to the textual address. Per Google Maps URL
// schema: https://developers.google.com/maps/documentation/urls/get-started
function directionsUrl(p) {
  let dest;
  if (p.coords && p.coords.length === 2) {
    dest = `${p.coords[0]},${p.coords[1]}`;
  } else if (p.address) {
    dest = p.address;
  } else if (p.center) {
    dest = `${p.center} Philadelphia PA`;
  } else {
    dest = "";
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

/* ---------------- MODAL ---------------- */

function ProgramModal({ program, onClose }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const accent = colorFor(program.type);

  // Lock body scroll while modal is open + esc-to-close.
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  // Init Leaflet only when the modal is mounted -- keeps the page-load
  // weight off the main programs view.
  React.useEffect(() => {
    if (!program.coords || !containerRef.current) return;
    let cancelled = false;

    function init() {
      if (cancelled || !window.L || !containerRef.current || mapRef.current) return;
      const L = window.L;
      const map = L.map(containerRef.current, {
        center: program.coords,
        zoom: 15,
        zoomControl: false,
        attributionControl: true,
        scrollWheelZoom: false,
        dragging: true,
        doubleClickZoom: false,
      });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      const icon = L.divIcon({
        className: "pgm-modal-pin",
        html: `<div class="pgm-modal-pin-body" style="background:${accent}"><div class="pgm-modal-pin-dot"></div></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      L.marker(program.coords, { icon, interactive: false }).addTo(map);
      mapRef.current = map;
    }

    if (window.L) init();
    else {
      const interval = setInterval(() => { if (window.L) { clearInterval(interval); init(); } }, 50);
      return () => { cancelled = true; clearInterval(interval); if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    }
    return () => {
      cancelled = true;
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, [program.coords, accent]);

  const phoneHref = program.phone ? `tel:${program.phone.replace(/[^0-9+]/g, "")}` : null;
  const emailHref = program.email ? `mailto:${program.email}` : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(42,24,32,0.7)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, overflowY: "auto",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={program.name}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ maxWidth: 640, width: "100%", padding: 0, background: "var(--c-paper)", maxHeight: "92vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <div style={{ padding: "18px 22px", borderBottom: "2px solid var(--c-ink)", background: "var(--c-cream-deep)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <span className={`chip ${categoryChipClass(program.type)}`}>{program.type}</span>
            <div className="display" style={{ fontSize: 24, marginTop: 8, lineHeight: 1.05 }}>{program.name}</div>
            <div style={{ fontFamily: "var(--f-mono)", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--c-ink-soft)", marginTop: 6 }}>
              {program.center} &middot; {program.day}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "var(--c-paper)", border: "2px solid var(--c-ink)", borderRadius: 999,
              width: 36, height: 36, fontSize: 18, fontWeight: 800, cursor: "pointer",
              flexShrink: 0,
            }}
          >&times;</button>
        </div>

        <div style={{ overflowY: "auto", padding: 22, display: "flex", flexDirection: "column", gap: 18 }}>
          {program.description && (
            <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "var(--c-ink)", margin: 0, whiteSpace: "pre-line" }}>
              {program.description}
            </p>
          )}

          {program.coords ? (
            <div
              ref={containerRef}
              style={{ height: 220, width: "100%", border: "2px solid var(--c-ink)", borderRadius: 14, overflow: "hidden", background: "var(--c-cream-deep)", position: "relative", isolation: "isolate" }}
              aria-label={`Map of ${program.center}`}
            />
          ) : program.address ? (
            <div style={{ padding: 14, border: "2px solid var(--c-ink)", borderRadius: 14, background: "var(--c-cream-deep)", fontFamily: "var(--f-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--c-ink-soft)" }}>
              {program.address}
            </div>
          ) : null}

          <div className="pgm-modal-info">
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>Center</div>
              <div style={{ fontFamily: "var(--f-display)", fontWeight: 600, fontSize: 16 }}>{program.center}</div>
              {program.region && (
                <div style={{ fontSize: 13, color: "var(--c-ink-soft)" }}>{program.region}</div>
              )}
              {program.address && (
                <div style={{ fontSize: 13, color: "var(--c-ink-soft)", marginTop: 4 }}>{program.address}</div>
              )}
            </div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 4 }}>For info, contact</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {phoneHref && (
                  <a
                    href={phoneHref}
                    className="pgm-modal-link"
                    style={{
                      fontFamily: "var(--f-display)", fontWeight: 700, fontSize: 18,
                      "--pgm-link-accent": accent,
                    }}
                  >
                    {program.phone}
                  </a>
                )}
                {emailHref && (
                  <a
                    href={emailHref}
                    className="pgm-modal-link"
                    style={{
                      fontFamily: "var(--f-display)", fontWeight: 600, fontSize: 14.5,
                      wordBreak: "break-all",
                      "--pgm-link-accent": accent,
                    }}
                  >
                    {program.email}
                  </a>
                )}
                {!phoneHref && !emailHref && (
                  <div style={{ fontFamily: "var(--f-mono)", fontSize: 13, color: "var(--c-ink-soft)" }}>
                    Call PPR: 215-685-3585
                  </div>
                )}
              </div>
            </div>
          </div>

          {(program.coords || program.address) && (
            <a
              href={directionsUrl(program)}
              target="_blank"
              rel="noopener noreferrer"
              className="pgm-directions-btn"
              style={{ "--btn-accent": accent }}
            >
              Get directions <span aria-hidden="true">&rarr;</span>
            </a>
          )}
        </div>
      </div>

      <style>{`
        .pgm-modal-pin { position: relative; }
        .pgm-modal-pin-body {
          width: 22px; height: 22px;
          border-radius: 50%;
          border: 2px solid var(--c-ink);
          box-shadow: 1px 1px 0 var(--c-ink);
          display: flex; align-items: center; justify-content: center;
        }
        .pgm-modal-pin-dot {
          width: 6px; height: 6px;
          background: var(--c-paper);
          border-radius: 50%;
        }

        .pgm-modal-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }
        @media (max-width: 540px) {
          .pgm-modal-info { grid-template-columns: 1fr; }
        }

        /* CR #278: phone + email links inside the program modal.
           Browser defaults paint :visited tel:/mailto: anchors blue/purple,
           which JJ flagged. We pin every state to ink (effectively black on
           the default palette) and add a clear hover affordance so users
           still know these are clickable. */
        .pgm-modal-link,
        .pgm-modal-link:link,
        .pgm-modal-link:visited,
        .pgm-modal-link:active {
          color: var(--c-ink);
          text-decoration: underline;
          text-decoration-color: var(--pgm-link-accent, var(--c-ink));
          text-decoration-thickness: 2px;
          text-underline-offset: 4px;
          transition: color 120ms ease, text-decoration-thickness 120ms ease;
        }
        .pgm-modal-link:hover,
        .pgm-modal-link:focus-visible {
          color: var(--pgm-link-accent, var(--c-ink));
          text-decoration-thickness: 3px;
          outline: none;
        }

        /* Directions CTA. Bigger, accent-colored, full-width on mobile so
           it's a clear primary action on the modal. */
        .pgm-directions-btn {
          display: inline-flex; align-items: center; justify-content: center;
          gap: 8px;
          padding: 14px 22px; min-height: 48px;
          background: var(--btn-accent, var(--c-ink));
          color: var(--c-paper);
          border: 2px solid var(--c-ink);
          border-radius: 999px;
          box-shadow: 3px 3px 0 var(--c-ink);
          font-family: var(--f-display);
          font-weight: 700; font-size: 15px;
          text-decoration: none;
          transition: transform 120ms ease, box-shadow 120ms ease;
          align-self: flex-start;
        }
        .pgm-directions-btn:hover {
          transform: translate(-2px, -2px);
          box-shadow: 5px 5px 0 var(--c-ink);
        }
        @media (max-width: 540px) {
          .pgm-directions-btn { width: 100%; align-self: stretch; }
        }
      `}</style>
    </div>
  );
}

Object.assign(window, { ProgramsPage });
